import type { FinancialTransactionFormValues } from "gsintegrations";
import type { FormChatState } from "../repositories/formChat.repository";

export type FinancialChatFlow = "income" | "outcome";

const PAYMENT_METHODS = [
  "dinheiro",
  "pix",
  "cartao_debito",
  "cartao_credito",
  "transferencia",
  "boleto",
  "cheque",
] as const;

type PaymentMethod = (typeof PAYMENT_METHODS)[number];

const isPaymentMethod = (value: unknown): value is PaymentMethod =>
  typeof value === "string" &&
  (PAYMENT_METHODS as readonly string[]).includes(value);

/**
 * Maps the chat form state to the payload the transaction form sends to
 * `POST/PUT /incoming` and `/outcome`. Returns null while required fields are missing.
 */
export const buildFinancialTransactionPayload = (
  state: FormChatState,
  flow: FinancialChatFlow,
): FinancialTransactionFormValues | null => {
  const description = state.description?.trim();
  const amount = Number(state.amount);
  if (
    !description ||
    !Number.isFinite(amount) ||
    amount <= 0 ||
    !state.categoryId ||
    !state.accountId ||
    !isPaymentMethod(state.paymentMethod)
  ) {
    return null;
  }

  const isOutcome = flow === "outcome";
  const inventoryItemId =
    isOutcome && state.inventoryItemId ? state.inventoryItemId : undefined;
  const quantity = Math.floor(Number(state.purchasedQuantity));
  const purchasedQuantity =
    inventoryItemId && Number.isFinite(quantity) && quantity >= 1
      ? quantity
      : undefined;
  const isRecurring = isOutcome && Boolean(state.isRecurring);
  const isSharedWithMembers = isOutcome && Boolean(state.isSharedWithMembers);
  const dueDate = state.dueDate ?? "";
  // With a purchased quantity the chat amount is the unit price; the outcome stores the total paid.
  const totalAmount = purchasedQuantity ? amount * purchasedQuantity : amount;

  // The API validates subcategoryId as a uuid, so a category without subcategories omits it.
  return {
    transactionType: isOutcome ? "saida" : "entrada",
    value: Math.round(totalAmount * 100) / 100,
    description,
    categoryId: state.categoryId,
    ...(state.subcategoryId ? { subcategoryId: state.subcategoryId } : {}),
    hasDueDate: Boolean(dueDate),
    dueDate,
    paymentMethod: state.paymentMethod,
    accountId: state.accountId,
    isRecurring,
    installments: isRecurring ? Math.max(1, state.installments ?? 1) : 1,
    observation: state.observation ?? "",
    settlementStatus: state.settlementStatus ?? "PROJECTED",
    correctionReason: state.correctionReason?.trim() || undefined,
    isSharedWithMembers,
    shareReason: isSharedWithMembers ? state.shareReason || undefined : undefined,
    inventoryItemId,
    purchasedQuantity,
  } as FinancialTransactionFormValues;
};
